import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useStockholmEventTracking } from "../lib/analytics";

export const MAX_SHORTLIST_UNITS = 3;
const STORAGE_KEY = "hemtjanstistockholm:shortlist:v1";
const SAFE_ID = /^[a-zA-Z0-9_-]{1,128}$/;

function sanitizeIds(
  values: readonly string[],
  limit = MAX_SHORTLIST_UNITS,
): string[] {
  const ids: string[] = [];
  for (const value of values) {
    const id = value.trim();
    if (!SAFE_ID.test(id) || ids.includes(id)) continue;
    ids.push(id);
    if (ids.length === limit) break;
  }
  return ids;
}

function sameIds(left: readonly string[], right: readonly string[]): boolean {
  return (
    left.length === right.length &&
    left.every((value, index) => value === right[index])
  );
}

function persistIds(ids: readonly string[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // Storage can be blocked by browser privacy settings. The shortlist still
    // works for the current page session through React state.
  }
}

function parseStored(value: string | null): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value) as unknown;
    return Array.isArray(parsed)
      ? sanitizeIds(
          parsed.filter((entry): entry is string => typeof entry === "string"),
        )
      : [];
  } catch {
    return [];
  }
}

function readStoredIds(): string[] {
  if (typeof window === "undefined") return [];
  try {
    return parseStored(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return [];
  }
}

interface ShortlistContextValue {
  providerIds: string[];
  isHydrated: boolean;
  isSaved: (providerId: string) => boolean;
  toggle: (providerId: string) => "added" | "removed" | "full";
  remove: (providerId: string) => void;
  clear: () => void;
  reconcileAvailable: (availableProviderIds: readonly string[]) => void;
  compareHref: string;
}

const ShortlistContext = createContext<ShortlistContextValue | null>(null);

export function ShortlistProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const trackStockholmEvent = useStockholmEventTracking();
  const locationParams = new URLSearchParams(location.search);
  const hasUrlState = locationParams.has("compare");
  const fromUrl = sanitizeIds((locationParams.get("compare") ?? "").split(","));
  const [providerIds, setProviderIds] = useState<string[]>(fromUrl);
  const [isHydrated, setIsHydrated] = useState(hasUrlState);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.has("compare")) {
      const next = sanitizeIds((params.get("compare") ?? "").split(","));
      setProviderIds((current) => (sameIds(current, next) ? current : next));
      persistIds(next);
      setIsHydrated(true);
      return;
    }
    const stored = readStoredIds();
    setProviderIds((current) => (sameIds(current, stored) ? current : stored));
    setIsHydrated(true);
  }, [location.search]);

  useEffect(() => {
    if (!isHydrated) return;
    persistIds(providerIds);
  }, [isHydrated, providerIds]);

  const updateProviderIds = useCallback(
    (values: readonly string[]) => {
      const next = sanitizeIds(values);
      setProviderIds((current) => (sameIds(current, next) ? current : next));
      persistIds(next);

      if (location.pathname !== "/jamfor") return;
      const params = new URLSearchParams(location.search);
      if (next.length > 0) params.set("compare", next.join(","));
      else params.delete("compare");
      const nextSearch = params.toString();
      const currentSearch = location.search.replace(/^\?/, "");
      if (nextSearch === currentSearch) return;
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : "",
        },
        { replace: true },
      );
    },
    [location.pathname, location.search, navigate],
  );

  const isSaved = useCallback(
    (providerId: string) => providerIds.includes(providerId),
    [providerIds],
  );

  const toggle = useCallback(
    (providerId: string) => {
      if (!SAFE_ID.test(providerId)) return "full" as const;
      if (providerIds.includes(providerId)) {
        updateProviderIds(providerIds.filter((id) => id !== providerId));
        trackStockholmEvent("shortlist_remove", {
          provider_id: providerId,
          source_surface: "shortlist",
        });
        return "removed" as const;
      }
      if (providerIds.length >= MAX_SHORTLIST_UNITS) return "full" as const;
      updateProviderIds([...providerIds, providerId]);
      trackStockholmEvent("shortlist_add", {
        provider_id: providerId,
        source_surface: "shortlist",
      });
      return "added" as const;
    },
    [providerIds, trackStockholmEvent, updateProviderIds],
  );

  const remove = useCallback(
    (providerId: string) => {
      updateProviderIds(providerIds.filter((id) => id !== providerId));
      trackStockholmEvent("shortlist_remove", {
        provider_id: providerId,
        source_surface: "shortlist",
      });
    },
    [providerIds, trackStockholmEvent, updateProviderIds],
  );

  const clear = useCallback(() => updateProviderIds([]), [updateProviderIds]);
  const reconcileAvailable = useCallback(
    (availableProviderIds: readonly string[]) => {
      const available = new Set(availableProviderIds);
      const params = new URLSearchParams(location.search);
      const candidates =
        location.pathname === "/jamfor" && params.has("compare")
          ? sanitizeIds((params.get("compare") ?? "").split(","), Infinity)
          : providerIds;
      updateProviderIds(candidates.filter((id) => available.has(id)));
    },
    [location.pathname, location.search, providerIds, updateProviderIds],
  );
  const compareHref = useMemo(() => {
    const params = new URLSearchParams();
    if (providerIds.length > 0) params.set("compare", providerIds.join(","));
    return `/jamfor${params.size > 0 ? `?${params.toString()}` : ""}`;
  }, [providerIds]);

  const value = useMemo(
    () => ({
      providerIds,
      isHydrated,
      isSaved,
      toggle,
      remove,
      clear,
      reconcileAvailable,
      compareHref,
    }),
    [
      clear,
      compareHref,
      isSaved,
      isHydrated,
      providerIds,
      reconcileAvailable,
      remove,
      toggle,
    ],
  );

  return (
    <ShortlistContext.Provider value={value}>
      {children}
    </ShortlistContext.Provider>
  );
}

export function useShortlist(): ShortlistContextValue {
  const context = useContext(ShortlistContext);
  if (!context) {
    throw new Error("useShortlist must be used inside ShortlistProvider");
  }
  return context;
}
